/**
 * One square per fiscal year, oldest to newest, coloured by that year's worst
 * severity.
 *
 * Rendered ONLY when the years actually differ. A green FY2017 beside amber
 * FY2018-FY2026 says "something changed in 2018" at a glance and no sentence
 * achieves that — but a source-level problem paints all ten squares the same
 * colour, which carries nothing the headline has not already given. Ten
 * identical squares is decoration.
 *
 * The gate lives inside the component so no future caller can reintroduce a
 * uniform, information-free strip.
 */

import Tooltip from '../../ui/Tooltip';
import type { ValidationPeriod } from '../../../types/dataQuality';
import { formatFiscalYear } from '../utils/dataQualityFormat';
import { periodProfile } from './dqSelectors';
import { STATUS_TOKENS } from './dqTokens';

/** "FY2024: 39 fields, 1 warning" — pluralised, silent about zero counts. */
function periodTooltip(period: ValidationPeriod): string {
  const parts = [`${period.fields} field${period.fields === 1 ? '' : 's'}`];
  if (period.errors > 0) parts.push(`${period.errors} error${period.errors === 1 ? '' : 's'}`);
  if (period.warnings > 0)
    parts.push(`${period.warnings} warning${period.warnings === 1 ? '' : 's'}`);
  if (period.errors === 0 && period.warnings === 0) parts.push('no issues');
  return `${formatFiscalYear(period.year)}: ${parts.join(', ')}`;
}

export interface YearStripProps {
  periods: ValidationPeriod[] | null | undefined;
  onSelectYear?: (year: number) => void;
  /** Suppress the "all years identical" gate — used where the strip is the point. */
  alwaysShow?: boolean;
}

export default function YearStrip({
  periods,
  onSelectYear,
  alwaysShow = false,
}: YearStripProps) {
  const rows = periodProfile(periods);
  if (rows.length === 0) return null;

  if (!alwaysShow && new Set(rows.map((r) => r.status)).size < 2) return null;

  return (
    <div>
      <p className="mb-1.5 text-xs font-bold uppercase tracking-widest text-neutral-400">
        Coverage by year
      </p>
      <div className="flex flex-wrap gap-1">
        {rows.map((row) => {
          const token = STATUS_TOKENS[row.status];
          const square = (
            <div className="flex w-9 flex-col items-center gap-1">
              <span className={`h-5 w-full rounded-sm ${token.dot}`} />
              {/* "26", not "FY2026" — the strip is a shape to scan, not a table. */}
              <span className="text-[10px] leading-none text-neutral-400">
                {String(row.year).slice(-2)}
              </span>
            </div>
          );

          return (
            <Tooltip key={row.year} content={periodTooltip(row)} position="top">
              {onSelectYear ? (
                <button
                  type="button"
                  onClick={() => onSelectYear(row.year)}
                  aria-label={periodTooltip(row)}
                  className="rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500"
                >
                  {square}
                </button>
              ) : (
                square
              )}
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
